/**
 * Stone Investment Sort Control
 * 
 * Elegant sort dropdown with Stone Investment's luxury aesthetic:
 * - Greyscale colour palette
 * - Montserrat typography
 * - Cinematic transitions
 */

'use client';

import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export interface SortOption {
  value: string;
  label: string;
}

export interface StoneSortControlProps {
  /**
   * Available sort options
   */
  options: SortOption[];
  /**
   * Currently selected value
   */
  value: string;
  /**
   * Callback when selection changes
   */
  onChange: (value: string) => void;
  /**
   * Label text
   */
  label?: string;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * StoneSortControl - Elegant sort dropdown with Stone Investment styling
 */
export function StoneSortControl({
  options,
  value,
  onChange,
  label = 'Sort by',
  className = '',
}: StoneSortControlProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const selectedOption = options.find(opt => opt.value === value) || options[0];
  
  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };
  
  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 h-10 px-3 text-stone-600 hover:text-stone-900 transition-colors"
      >
        <span className="text-[10px] uppercase tracking-[0.15em] text-stone-400 font-sans">
          {label}
        </span>
        <span className="text-xs font-sans font-medium text-stone-800">
          {selectedOption?.label}
        </span>
        <ChevronDown 
          size={14} 
          className={`text-stone-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
          strokeWidth={1.5}
        />
      </button>
      
      {/* Dropdown */}
      {isOpen && (
        <div 
          className="absolute top-full right-0 sm:left-0 sm:right-auto mt-1 min-w-0 w-full sm:min-w-[200px] max-w-[calc(100vw-2rem)] bg-white border border-stone-200 shadow-lg z-50 animate-in fade-in slide-in-from-top-2 duration-200"
        >
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => handleSelect(option.value)}
              className={`
                w-full flex items-center justify-between px-4 py-3 text-left
                hover:bg-stone-50 transition-colors
                ${option.value === value ? 'bg-stone-50' : ''}
              `}
            >
              <span className="text-sm font-sans text-stone-700">
                {option.label}
              </span>
              {option.value === value && (
                <Check size={16} className="text-stone-900" strokeWidth={1.5} />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default StoneSortControl;
